import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Descriptions, Empty, Select, Spin, Table, Tag, Tree, Typography, message } from "antd";
import {
  ApiOutlined,
  ArrowLeftOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import intl from "react-intl-universal";
import MarkdownPreview from "@/components/MarkdownPreview";
import {
  getSkillVersionDownloadUrl,
  skillAggregateDetail,
  skillContent,
  skillDetail,
  skillInvokeSpec,
  skillMetadata,
  skillVersionFileContent,
  skillVersionFiles,
  skillVersionReadme,
  skillVersions,
  submitSkillAudit,
  setDefaultSkillVersion,
  updateSkillVersionStatus,
} from "@/apis/skillhub";
import styles from "./index.module.scss";

const { Paragraph } = Typography;

const getText = (key) => intl.get(`SkillHub.${key}`);

const getPrettyText = (value) => {
  if (!value) return "";
  if (typeof value !== "string") return JSON.stringify(value, null, 2);
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (e) {
    return value;
  }
};

const normalizeSkillContent = (data, fallback = null) => {
  if (!data) return fallback;
  if (typeof data === "string") {
    return {
      path: fallback?.path || "skill.md",
      content: data,
    };
  }
  if (typeof data.content === "string") {
    return {
      ...data,
      path: data.path || data.name || fallback?.path || "skill.md",
      content: data.content,
    };
  }
  if (typeof data.markdownContent === "string") {
    return {
      ...data,
      path: data.path || data.name || fallback?.path || "skill.md",
      content: data.markdownContent,
    };
  }
  if (typeof data.text === "string") {
    return {
      ...data,
      path: data.path || data.name || fallback?.path || "skill.md",
      content: data.text,
    };
  }
  if (typeof data.markdown === "string") {
    return {
      ...data,
      path: data.path || data.name || fallback?.path || "skill.md",
      content: data.markdown,
    };
  }
  if (typeof data.skillMd === "string") {
    return {
      ...data,
      path: data.path || data.name || fallback?.path || "skill.md",
      content: data.skillMd,
    };
  }
  return fallback;
};

const normalizeMetadata = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return {};
    }
  }
  if (typeof value === "object") return value;
  return {};
};

const renderField = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

const buildTreeData = (files = []) => {
  const root = {};
  files.forEach((file) => {
    const parts = String(file.path || file.name || "")
      .split("/")
      .filter(Boolean);
    let current = root;
    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join("/");
      if (!current[part]) {
        current[part] = {
          title: part,
          key: path,
          selectable: index === parts.length - 1 && file.type !== "directory",
          childrenMap: {},
          meta: index === parts.length - 1 ? file : null,
        };
      }
      current = current[part].childrenMap;
    });
  });

  const walk = (nodes) =>
    Object.values(nodes).map((node) => {
      const children = walk(node.childrenMap);
      return {
        title: node.title,
        key: node.key,
        selectable: node.selectable,
        children,
        meta: node.meta,
      };
    });

  return walk(root);
};

const SkillDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const skillUuid = searchParams.get("id");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState({});
  const [versions, setVersions] = useState([]);
  const [versionDetail, setVersionDetail] = useState(null);
  const [readme, setReadme] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [files, setFiles] = useState([]);
  const [currentVersion, setCurrentVersion] = useState("");
  const [readmeLoading, setReadmeLoading] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [invokeSpec, setInvokeSpec] = useState(null);

  const statusMap = {
    0: { text: getText("Draft"), color: "default" },
    1: { text: getText("Published"), color: "green" },
    2: { text: getText("Disabled"), color: "red" },
  };

  const auditStatusMap = {
    0: { text: getText("Not submitted"), color: "default" },
    1: { text: getText("Pending Review"), color: "processing" },
    2: { text: getText("Approved"), color: "green" },
    3: { text: getText("Rejected"), color: "red" },
  };

  const versionStatusMap = {
    0: { text: getText("Disabled"), color: "red" },
    1: { text: getText("Enabled"), color: "green" },
  };

  const tags = useMemo(() => {
    const rawTags = detail.tags || metadata.tags;
    if (!rawTags) return [];
    if (Array.isArray(rawTags)) return rawTags.filter(Boolean).map((item) => String(item));
    return String(rawTags).split(",").map((item) => item.trim()).filter(Boolean);
  }, [detail.tags, metadata.tags]);

  const treeData = useMemo(() => buildTreeData(files), [files]);
  const activeVersion = currentVersion || versionDetail?.version || detail.version;
  const activeManifest = getPrettyText(versionDetail?.manifest || detail.manifest || metadata.manifest);
  const activeParameterSchema = getPrettyText(versionDetail?.parameterSchema || detail.parameterSchema || metadata.parameterSchema);

  const loadSkillContent = useCallback(
    (fallback = null) => {
      setReadmeLoading(true);
      return skillContent(skillUuid)
        .then((res) => normalizeSkillContent(res.data, fallback))
        .catch(() => fallback)
        .finally(() => setReadmeLoading(false));
    },
    [skillUuid]
  );

  const fetchData = useCallback(() => {
    if (!skillUuid) return;
    setLoading(true);
    skillDetail(skillUuid)
      .then((res) => {
        const rawDetail = res.data || {};
        const nextDetail = rawDetail.skill ? { ...rawDetail.skill, ...rawDetail } : rawDetail;
        const nextMetadata = normalizeMetadata(nextDetail.metadata);
        const fallbackReadme = normalizeSkillContent(nextDetail, null);
        setDetail(nextDetail);
        setMetadata(nextMetadata);
        setReadme(fallbackReadme);
        setCurrentVersion((prev) => prev || nextDetail.version || nextMetadata.version || currentVersion);

        return Promise.all([
          skillAggregateDetail(skillUuid, currentVersion ? { version: currentVersion } : undefined).catch(() => ({ data: null })),
          skillVersions(skillUuid).catch(() => ({ data: [] })),
          skillInvokeSpec(skillUuid, currentVersion ? { version: currentVersion } : undefined).catch(() => ({ data: null })),
          skillMetadata(skillUuid).catch(() => ({ data: null })),
          loadSkillContent(fallbackReadme).then((content) => ({ data: content })),
        ]);
      })
      .then(([aggregateRes, versionRes, specRes, metadataRes, contentRes]) => {
        const data = aggregateRes.data || {};
        const aggregateSkill = data.skill || {};
        const nextVersion = data.version || null;
        setDetail((prev) => ({ ...aggregateSkill, ...prev }));
        setMetadata((prev) => ({ ...normalizeMetadata(metadataRes.data), ...prev }));
        setVersionDetail(nextVersion);
        setFiles(data.files || []);
        setVersions(versionRes.data || data.versions || (nextVersion ? [nextVersion] : []));
        setCurrentVersion((prev) => prev || nextVersion?.version || aggregateSkill.version || "");
        setInvokeSpec(specRes.data || null);
        setReadme((prev) => contentRes.data || prev || data.readme || null);
      })
      .catch(() => {
        return Promise.all([
          skillAggregateDetail(skillUuid, currentVersion ? { version: currentVersion } : undefined).catch(() => ({ data: null })),
          skillVersions(skillUuid).catch(() => ({ data: [] })),
          skillInvokeSpec(skillUuid).catch(() => ({ data: null })),
        ]).then(([aggregateRes, versionRes, specRes]) => {
          const aggregateData = aggregateRes.data || {};
          const nextDetail = aggregateData.skill || {};
          const nextVersions = versionRes.data || [];
          const nextVersion =
            nextVersions.find((item) => item.version === currentVersion) ||
            nextVersions.find((item) => item.isDefault) ||
            nextVersions[0] ||
            null;
          const nextVersionValue = nextVersion?.version || nextDetail.version || currentVersion;
          setDetail(nextDetail);
          setMetadata(normalizeMetadata(nextDetail.metadata));
          setVersions(nextVersions);
          setVersionDetail(nextVersion);
          setCurrentVersion(nextVersionValue);
          setInvokeSpec(specRes.data || null);
          if (!nextVersionValue) return;
          Promise.all([
            skillVersionReadme(skillUuid, nextVersionValue).catch(() => ({ data: null })),
            skillVersionFiles(skillUuid, nextVersionValue).catch(() => ({ data: [] })),
          ]).then(([readmeRes, filesRes]) => {
            const fallbackReadme = readmeRes.data || null;
            setReadme(fallbackReadme);
            setFiles(filesRes.data || []);
            return loadSkillContent(fallbackReadme).then(setReadme);
          });
        });
      })
      .catch(() => {
        message.error(getText("Failed to load skill detail"));
      })
      .finally(() => setLoading(false));
  }, [currentVersion, loadSkillContent, skillUuid]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const changeDefaultVersion = (version) => {
    setDefaultSkillVersion(skillUuid, version).then(() => {
      message.success(getText("Default version updated"));
      fetchData();
    });
  };

  const changeVersionStatus = (version, status) => {
    updateSkillVersionStatus(skillUuid, version, status).then(() => {
      message.success(getText("Version status updated"));
      fetchData();
    });
  };

  const submitReview = () => {
    submitSkillAudit(skillUuid, {
      version: activeVersion,
      message: getText("Submit for review"),
    }).then(() => {
      message.success(getText("Submitted for review"));
      fetchData();
    });
  };

  const previewFile = (_, info) => {
    const path = info.node?.key;
    if (!path || !activeVersion || info.node?.children?.length) return;
    setFileLoading(true);
    skillVersionFileContent(skillUuid, activeVersion, path)
      .then((res) => {
        setFilePreview(res.data || null);
      })
      .catch(() => {
        message.error(getText("File preview failed"));
      })
      .finally(() => setFileLoading(false));
  };

  const downloadVersion = async (version) => {
    try {
      const url = await getSkillVersionDownloadUrl(skillUuid, version);
      window.location.assign(url);
    } catch (error) {
      // The shared client displays authorization errors and opens login when required.
    }
  };

  const columns = [
    {
      title: getText("Version"),
      dataIndex: "version",
      render: (text, record) => (
        <span>
          {text} {record.isDefault ? <Tag color="magenta">{getText("Default")}</Tag> : null}
        </span>
      ),
    },
    {
      title: getText("Runtime"),
      dataIndex: "runtime",
    },
    {
      title: getText("Entrypoint"),
      dataIndex: "entrypoint",
    },
    {
      title: getText("Package"),
      dataIndex: "packageFileName",
      render: (text) => text || "-",
    },
    {
      title: intl.get("Status"),
      dataIndex: "status",
      render: (value) => {
        const status = versionStatusMap[value] || versionStatusMap[0];
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: getText("Audit Status"),
      dataIndex: "auditStatus",
      render: (value) => {
        const status = auditStatusMap[value] || auditStatusMap[0];
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: getText("Actions"),
      render: (_, record) => (
        <div className={styles.tableActions}>
          {!record.isDefault && (
            <Button size="small" onClick={() => changeDefaultVersion(record.version)}>
              {getText("Set default")}
            </Button>
          )}
          <Button
            size="small"
            disabled={record.isDefault && record.status === 1}
            onClick={() => changeVersionStatus(record.version, record.status === 1 ? 0 : 1)}
          >
            {record.status === 1 ? getText("Disable") : getText("Enable")}
          </Button>
          <Button size="small" onClick={() => downloadVersion(record.version)}>
            {getText("Download")}
          </Button>
        </div>
      ),
    },
  ];

  if (!skillUuid) {
    return (
      <div className={classNames("container", styles.detailPage)}>
        <Empty description={getText("Missing skill id")} />
      </div>
    );
  }

  return (
    <div className={styles.detailWrap}>
      <Spin spinning={loading}>
        <div className={classNames("container", styles.detailPage)}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/skillhub")}>
            {getText("Back to Open Skill Hub")}
          </Button>

          <section className={styles.header}>
            <div>
              <div className={styles.titleLine}>
                <h1>{detail.title || detail.name || metadata.title || getText("Skill")}</h1>
                <Tag color={(statusMap[detail.status] || statusMap[0]).color}>
                  {(statusMap[detail.status] || statusMap[0]).text}
                </Tag>
                <Tag color={(auditStatusMap[detail.auditStatus] || auditStatusMap[0]).color}>
                  {(auditStatusMap[detail.auditStatus] || auditStatusMap[0]).text}
                </Tag>
              </div>
              <p>{detail.description || metadata.description || getText("No description")}</p>
              <div className={styles.tags}>
                {tags.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </div>
            </div>
            <div className={styles.headerActions}>
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                onClick={() => navigate(`/skillhub/upload?id=${skillUuid}`)}
              >
                {getText("Upload Version")}
              </Button>
              {[0, 3].includes(detail.auditStatus) ? (
                <Button icon={<FileTextOutlined />} onClick={submitReview}>
                  {getText("Submit for review")}
                </Button>
              ) : null}
              {detail.resourceUuid ? (
                <Button
                  icon={<DatabaseOutlined />}
                  onClick={() => {
                    window.location.href = `${process.env.PUBLIC_URL}/discover/detail?id=${detail.resourceUuid}`;
                  }}
                >
                  {getText("Discover Resource")}
                </Button>
              ) : null}
            </div>
          </section>

          <section className={styles.section}>
            <h2>{getText("Overview")}</h2>
            <Descriptions bordered column={2}>
              <Descriptions.Item label={getText("Skill UUID")}>{renderField(detail.skillUuid || metadata.skillUuid || skillUuid)}</Descriptions.Item>
              <Descriptions.Item label={getText("Version")}>{renderField(detail.version || metadata.version)}</Descriptions.Item>
              <Descriptions.Item label={getText("Runtime")}>{renderField(detail.runtime || metadata.runtime)}</Descriptions.Item>
              <Descriptions.Item label={getText("Entrypoint")}>{renderField(detail.entrypoint || metadata.entrypoint)}</Descriptions.Item>
              <Descriptions.Item label={getText("Author")}>{renderField(detail.author || metadata.author || detail.ownerName)}</Descriptions.Item>
              <Descriptions.Item label={getText("Owner")}>{renderField(detail.ownerName)}</Descriptions.Item>
              <Descriptions.Item label={getText("File UUID")}>{renderField(detail.fileUuid || metadata.fileUuid)}</Descriptions.Item>
              <Descriptions.Item label={getText("Service API")}>{renderField(detail.serviceApi || metadata.serviceApi)}</Descriptions.Item>
              <Descriptions.Item label={getText("Audit Status")}>
                {(auditStatusMap[detail.auditStatus] || auditStatusMap[0]).text}
              </Descriptions.Item>
              <Descriptions.Item label={getText("Package")} span={2}>
                {versionDetail?.packageFileName || detail.packageFileName || versionDetail?.packageFileUuid || detail.packageFileUuid || "-"}
              </Descriptions.Item>
            </Descriptions>
          </section>

          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2>{getText("Package Browser")}</h2>
              <Select
                value={activeVersion}
                options={versions.map((item) => ({
                  label: `${item.version}${item.isDefault ? ` (${getText("Default")})` : ""}`,
                  value: item.version,
                }))}
                onChange={(value) => {
                  setFilePreview(null);
                  setCurrentVersion(value);
                }}
              />
            </div>
            <div className={styles.packageBrowser}>
              <div className={styles.readme}>
                <div className={styles.browserTitle}>{readme?.path || getText("README")}</div>
                <Spin spinning={readmeLoading}>
                  {readme?.content ? (
                    <MarkdownPreview content={readme.content} />
                  ) : (
                    <Empty className={styles.emptyState} description={getText("No README found")} />
                  )}
                </Spin>
              </div>
              <div className={styles.files}>
                <div className={styles.browserTitle}>{getText("Files")}</div>
                {treeData.length ? (
                  <Tree
                    blockNode
                    showLine
                    treeData={treeData}
                    defaultExpandAll
                    onSelect={previewFile}
                  />
                ) : (
                  <Empty description={getText("No files found")} />
                )}
              </div>
            </div>
            <Spin spinning={fileLoading}>
              {filePreview ? (
                <div className={styles.filePreview}>
                  <div className={styles.browserTitle}>{filePreview.path}</div>
                  <pre>{filePreview.content}</pre>
                </div>
              ) : null}
            </Spin>
          </section>

          <section className={styles.section}>
            <h2>{getText("Version Detail")}</h2>
            <div className={styles.codeGrid}>
              <div>
                <div className={styles.browserTitle}>manifest</div>
                {activeManifest ? <pre>{activeManifest}</pre> : <Empty />}
              </div>
              <div>
                <div className={styles.browserTitle}>{getText("Parameter Schema")}</div>
                {activeParameterSchema ? <pre>{activeParameterSchema}</pre> : <Empty />}
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2>{getText("Agent Invoke Spec")}</h2>
            {invokeSpec ? (
              <div className={styles.specBox}>
                <div>
                  <ApiOutlined />
                  <span>{invokeSpec.runtime || detail.runtime || getText("Runtime")}</span>
                </div>
                <Paragraph copyable>{JSON.stringify(invokeSpec, null, 2)}</Paragraph>
              </div>
            ) : (
              <Empty />
            )}
          </section>

          <section className={styles.section}>
            <h2>{getText("Versions")}</h2>
            <Table
              rowKey={(record) => record.versionUuid || record.version}
              columns={columns}
              dataSource={versions}
              pagination={false}
            />
          </section>
        </div>
      </Spin>
    </div>
  );
};

export default SkillDetail;
